import React, { createContext, useContext, useState, useEffect } from 'react'
import type { Integration, IntegrationConfig } from '@/types/integration'
import { AVAILABLE_INTEGRATIONS } from '@/config/integrations'
import { fetchWithAuth } from '@/lib/api'

interface IntegrationContextValue {
  /** All available integrations */
  integrations: Integration[]

  /** Only enabled integrations */
  enabledIntegrations: Integration[]

  /** Only configured integrations (enabled + has credentials) */
  configuredIntegrations: Integration[]

  /** Get integration by ID */
  getIntegration: (id: string) => Integration | undefined

  /** Enable/disable an integration */
  toggleIntegration: (id: string, enabled: boolean) => Promise<void>

  /** Update integration configuration */
  updateIntegrationConfig: (id: string, config: Partial<IntegrationConfig>) => Promise<void>

  /** Check if integration is configured */
  isConfigured: (id: string) => boolean

  /** Loading state */
  loading: boolean
}

export const IntegrationContext = createContext<IntegrationContextValue | undefined>(undefined)

export const IntegrationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [integrations, setIntegrations] = useState<Integration[]>(AVAILABLE_INTEGRATIONS)
  const [configs, setConfigs] = useState<Record<string, IntegrationConfig>>({})
  const [loading, setLoading] = useState(true)

  // Load integration configurations on mount
  useEffect(() => {
    // Set up default configs for development/testing
    const defaultConfigs: Record<string, IntegrationConfig> = {
      mock: { id: 'mock', enabled: false, status: 'inactive' },
      basecamp: { id: 'basecamp', enabled: true, status: 'active' },
      'voice-memos': { id: 'voice-memos', enabled: true, status: 'active' },
      'product-board': { id: 'product-board', enabled: true, status: 'active' },
      'knowledge-hub': { id: 'knowledge-hub', enabled: false, status: 'inactive' },
    }

    // Update integrations with default configs
    const updatedIntegrations = AVAILABLE_INTEGRATIONS.map(integration => ({
      ...integration,
      enabled: defaultConfigs[integration.id]?.enabled ?? integration.enabled,
      configured: defaultConfigs[integration.id]?.status === 'active',
    }))

    console.log('[IntegrationProvider] Updated integrations:', updatedIntegrations.map(i => ({
      id: i.id,
      enabled: i.enabled,
      configured: i.configured,
      hasMainView: !!i.MainView
    })))

    setIntegrations(updatedIntegrations)
    setConfigs(defaultConfigs)
    setLoading(false)

    console.log('Integration configs loaded (development mode)')
  }, [])

  const enabledIntegrations = integrations.filter(i => i.enabled)
  const configuredIntegrations = integrations.filter(i => i.enabled && i.configured)

  const getIntegration = (id: string) => {
    return integrations.find(i => i.id === id)
  }

  const toggleIntegration = async (id: string, enabled: boolean) => {
    try {
      const response = await fetchWithAuth(`/api/integrations/${id}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      })

      if (response.ok) {
        setIntegrations(integrations.map(i =>
          i.id === id ? { ...i, enabled } : i
        ))
      }
    } catch (error) {
      console.error('Failed to toggle integration:', error)
      // Optimistic update even if backend fails (for now)
      setIntegrations(integrations.map(i =>
        i.id === id ? { ...i, enabled } : i
      ))
    }
  }

  const updateIntegrationConfig = async (id: string, config: Partial<IntegrationConfig>) => {
    try {
      const response = await fetchWithAuth(`/api/integrations/${id}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      })

      if (response.ok) {
        setConfigs(prev => ({
          ...prev,
          [id]: { ...prev[id], ...config } as IntegrationConfig,
        }))
      }
    } catch (error) {
      console.error('Failed to update integration config:', error)
    }
  }

  const isConfigured = (id: string) => {
    return configs[id]?.status === 'active'
  }

  return (
    <IntegrationContext.Provider
      value={{
        integrations,
        enabledIntegrations,
        configuredIntegrations,
        getIntegration,
        toggleIntegration,
        updateIntegrationConfig,
        isConfigured,
        loading,
      }}
    >
      {children}
    </IntegrationContext.Provider>
  )
}

export const useIntegrationsContext = () => {
  const context = useContext(IntegrationContext)
  if (!context) {
    throw new Error('useIntegrationsContext must be used within IntegrationProvider')
  }
  return context
}

